import React, {
    ComponentPropsWithoutRef,
    ForwardedRef,
    forwardRef,
    useContext,
    useRef,
} from 'react';
import { LinkProps, useHistory } from 'react-router-dom';
import { useForwardedRef } from '@givebutter/react-util';
import { TableConstants } from 'components/table/TableConstants';
import { TableCellContext, TableContext } from 'components/table/TableContext';
import { useIsStickyMediaQuery } from 'components/table/useIsStickyMediaQuery';
import { getComputedColumnProps, isStickyColumn } from 'components/table/util';
import { UnstyledLink } from 'components/unstyled-link';
import { componentOrFragmentFactory } from 'components/util';
import { twJoin, twMerge } from 'tailwind-merge';

interface TableDataProps extends ComponentPropsWithoutRef<'td'> {}

const TableData = forwardRef((props: TableDataProps, ref: ForwardedRef<HTMLTableCellElement>) => {
    const {
        className: classNameConsumer = '',
        children,
        onClick,
        onKeyDown,
        'aria-label': ariaLabel = 'Table cell',
        style = {},
        ...rest
    } = props;
    const history = useHistory();
    const isMobile = useIsStickyMediaQuery();
    const { isActionHidden, table, variant } = useContext(TableContext);
    const { cell } = useContext(TableCellContext);
    const tdRef = useForwardedRef(ref);
    const innerContentRef = useRef<HTMLDivElement>(null);
    const to = table.options.meta?.to?.(cell);
    const isLink = !!to && !cell.column.columnDef.meta?.ignoreLink;
    const isClickable = (!!onClick && !cell.column.columnDef.meta?.ignoreClick) || isLink;
    const LinkOrFragment = componentOrFragmentFactory(UnstyledLink, isLink);
    const className = twJoin(classNameConsumer, cell.column.columnDef.meta?.className);
    const tdClassName = 'px-4';
    const variantClass = variant === 'bordered' ? 'first:pl-6 last:pr-6' : '';
    const rowClassName = table.options.meta?.rowClassName;
    const isAction = cell.column.id === TableConstants.Actions;
    const actionColumnClass = twJoin(
        isActionHidden &&
            isAction &&
            'invisible [&_[data-state=open]]:bg-gray-300 [&_[data-state=open]]:visible group-hover:visible sticky right-0',
    );

    const isNotChildNodeTarget = (target: EventTarget) =>
        target === tdRef.current || target === innerContentRef.current;

    const { pinnedClassName } = getComputedColumnProps(table, cell.column);

    return (
        <td
            tabIndex={isClickable ? 0 : undefined}
            ref={tdRef}
            className={twMerge(
                `h-[3.25rem] border-t border-t-gray-100 bg-white text-sm group-first:border-none group-last:first:rounded-bl-md group-last:last:rounded-br-md`,
                variant === 'none' &&
                    'group-first:first:rounded-tl-md group-first:last:rounded-tr-md',
                !isLink && tdClassName,
                !isLink && variantClass,
                isLink && 'first:[&>*]:first:pl-6 last:[&>*]:last:pr-6',
                pinnedClassName,
                actionColumnClass,
                rowClassName,
                className,
            )}
            style={{
                left:
                    !isMobile && isStickyColumn(cell.column)
                        ? `var(${TableConstants.ColumnOffsetPropertyPrefix}-${cell.column.id})`
                        : undefined,
            }}
            onClick={
                isClickable
                    ? e => {
                          if (!!to && isNotChildNodeTarget(e.target)) {
                              history.push(to as string);
                          }

                          onClick?.(e);
                      }
                    : undefined
            }
            onKeyDown={
                isClickable
                    ? e => {
                          if (e.key === ' ' && isNotChildNodeTarget(e.target)) {
                              e.preventDefault();

                              if (to) {
                                  history.push(to as string);
                              } else {
                                  onClick?.(e as unknown as React.MouseEvent<HTMLTableCellElement>);
                              }
                          }

                          onKeyDown?.(e);
                      }
                    : undefined
            }
            aria-label={ariaLabel}
            {...rest}
        >
            <LinkOrFragment
                tabIndex={-1}
                className={twJoin('!flex h-[3.25rem] items-center', tdClassName)}
                to={to as LinkProps['to']}
                aria-label={'Table link'}
            >
                <div
                    ref={innerContentRef}
                    className={'flex h-6 flex-nowrap items-center whitespace-nowrap'}
                >
                    {children}
                </div>
            </LinkOrFragment>
        </td>
    );
});

export { TableData };
export type { TableDataProps };
